import type { LatLngTuple } from "@fortex/api";
import { distanceMeters, radians } from "./geodesy";

function perpendicularDistanceMeters(point: LatLngTuple, from: LatLngTuple, to: LatLngTuple): number {
  const scale = Math.cos(radians(from[0]));
  const ax = from[1] * scale;
  const ay = from[0];
  const bx = to[1] * scale;
  const by = to[0];
  const px = point[1] * scale;
  const py = point[0];
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) return distanceMeters(point, from);

  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSquared));
  const projected: LatLngTuple = [ay + t * dy, (ax + t * dx) / scale];

  return distanceMeters(point, projected);
}

function simplifyRange(points: LatLngTuple[], first: number, last: number, toleranceMeters: number, keep: boolean[]): void {
  let maxDistance = 0;
  let index = -1;

  for (let current = first + 1; current < last; current++) {
    const distance = perpendicularDistanceMeters(points[current], points[first], points[last]);

    if (distance > maxDistance) {
      maxDistance = distance;
      index = current;
    }
  }

  if (index < 0 || maxDistance <= toleranceMeters) return;

  keep[index] = true;
  simplifyRange(points, first, index, toleranceMeters, keep);
  simplifyRange(points, index, last, toleranceMeters, keep);
}

export function simplifyPoints(points: LatLngTuple[], toleranceMeters: number): LatLngTuple[] {
  if (points.length <= 2 || toleranceMeters <= 0) return [...points];

  const keep = points.map(() => false);

  keep[0] = true;
  keep[points.length - 1] = true;
  simplifyRange(points, 0, points.length - 1, toleranceMeters, keep);

  return points.filter((_, index) => keep[index]);
}
